import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  Calendar, Eye, Clock, Tag, ArrowLeft, Download, FileText,
  Lock, Image, ChevronLeft, ChevronRight, X, BookOpen
} from 'lucide-react';
import { AuthContext } from '../App';
import { isBlogAllowed } from './Blog';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const { user } = useContext(AuthContext);
  const blogAllowed = isBlogAllowed();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(`${API_URL}/api/blog/posts/${slug}`, { headers });
        if (res.status === 403) {
          setError('exclusive');
          return;
        }
        if (res.status === 404) {
          setError('notfound');
          return;
        }
        if (!res.ok) throw new Error();

        const data = await res.json();
        setPost(data.post);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('error');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const getReadTime = (contenido) => {
    if (!contenido) return '1 min';
    const words = contenido.replace(/<[^>]+>/g, '').split(/\s+/).length;
    return `${Math.max(1, Math.ceil(words / 200))} min`;
  };

  const images = post?.archivos?.filter(a => a.es_imagen) || [];
  const documents = post?.archivos?.filter(a => !a.es_imagen) || [];

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const navigateLightbox = (dir) => {
    setLightboxIndex(prev => {
      const next = prev + dir;
      if (next < 0) return images.length - 1;
      if (next >= images.length) return 0;
      return next;
    });
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKey = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen]);

  if (!blogAllowed) {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-loading">
          <div className="skeleton-post-hero" />
          <div className="skeleton-post-content">
            <div className="skeleton-line short" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
            <div className="skeleton-line medium" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </div>
      </div>
    );
  }

  if (error === 'exclusive') {
    return (
      <div className="blog-post-page">
        <div className="blog-post-error">
          <Lock size={48} />
          <h2>Contenido exclusivo</h2>
          <p>Esta publicación es exclusiva para clientes de CentralSalud. Inicia sesión con tu cuenta de cliente para acceder.</p>
          <Link to="/blog" className="blog-btn-primary"><ArrowLeft size={16} /> Volver al blog</Link>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-error">
          <BookOpen size={48} />
          <h2>Publicación no encontrada</h2>
          <p>La publicación que buscas no existe o ha sido eliminada.</p>
          <Link to="/blog" className="blog-btn-primary"><ArrowLeft size={16} /> Volver al blog</Link>
        </div>
      </div>
    );
  }

  // Detect block-based content and parse accordingly
  const isBlockContent = post.contenido && post.contenido.startsWith('<!-- BLOCKS_JSON:');
  const renderedContent = isBlockContent
    ? post.contenido.replace(/^<!--\s*BLOCKS_JSON:[\s\S]*?-->\s*/, '')
    : post.contenido;

  return (
    <div className="blog-post-page">
      {/* Back link */}
      <div className="blog-post-nav">
        <Link to="/blog" className="blog-back-link">
          <ArrowLeft size={16} /> Volver al blog
        </Link>
        {user?.permisos === 1 && (
          <Link to={`/blog/admin?edit=${post.id}`} className="blog-edit-link">
            Editar publicación
          </Link>
        )}
      </div>

      {/* Hero image */}
      {post.imagen_portada && (
        <div className="blog-post-hero">
          <img src={post.imagen_portada} alt={post.titulo} />
        </div>
      )}

      {/* Post content */}
      <article className={`blog-post-article${isBlockContent ? ' blog-post-wide' : ''}`}>
        <header className="blog-post-header">
          {post.categoria_nombre && (
            <Link
              to={`/blog?categoria=${post.categoria_slug}`}
              className="blog-post-category"
              style={{ backgroundColor: post.categoria_color }}
            >
              {post.categoria_nombre}
            </Link>
          )}

          <h1>{post.titulo}</h1>

          {post.extracto && (
            <p className="blog-post-excerpt">{post.extracto}</p>
          )}

          <div className="blog-post-meta">
            <span><Calendar size={14} /> {formatDate(post.published_at)}</span>
            <span><Clock size={14} /> {getReadTime(post.contenido)} de lectura</span>
            <span><Eye size={14} /> {post.vistas} vistas</span>
            {post.autor_nombre && <span>Por {post.autor_nombre}</span>}
          </div>

          {post.exclusivo_clientes === 1 && (
            <div className="blog-post-exclusive-badge">
              <Lock size={14} /> Contenido exclusivo para clientes
            </div>
          )}

          {post.etiquetas && (
            <div className="blog-post-tags">
              {post.etiquetas.split(',').map(tag => (
                <Link
                  key={tag.trim()}
                  to={`/blog?etiqueta=${tag.trim()}`}
                  className="blog-post-tag"
                >
                  <Tag size={12} /> {tag.trim()}
                </Link>
              ))}
            </div>
          )}
        </header>

        {/* Main content */}
        <div
          className="blog-post-body"
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />

        {/* Image gallery - only for non-block posts (block posts have images inline) */}
        {!isBlockContent && images.length > 0 && (
          <div className="blog-post-gallery">
            <h3><Image size={18} /> Galería de imágenes</h3>
            <div className="blog-gallery-grid">
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  className="blog-gallery-item"
                  onClick={() => openLightbox(idx)}
                >
                  <img src={img.url} alt={img.nombre_original} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        {documents.length > 0 && (
          <div className="blog-post-documents">
            <h3><FileText size={18} /> Documentos adjuntos</h3>
            <div className="blog-docs-list">
              {documents.map(doc => (
                <a
                  key={doc.id}
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="blog-doc-item"
                >
                  <div className="blog-doc-icon">
                    <Download size={18} />
                  </div>
                  <div className="blog-doc-info">
                    <span className="blog-doc-name">{doc.nombre_original}</span>
                    <span className="blog-doc-size">{(doc.tamano / 1024).toFixed(0)} KB</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Lightbox */}
      {lightboxOpen && images.length > 0 && (
        <div className="blog-lightbox" onClick={closeLightbox}>
          <div className="blog-lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="blog-lightbox-close" onClick={closeLightbox}><X size={24} /></button>
            {images.length > 1 && (
              <>
                <button className="blog-lightbox-prev" onClick={() => navigateLightbox(-1)}><ChevronLeft size={32} /></button>
                <button className="blog-lightbox-next" onClick={() => navigateLightbox(1)}><ChevronRight size={32} /></button>
              </>
            )}
            <img src={images[lightboxIndex].url} alt={images[lightboxIndex].nombre_original} />
            <div className="blog-lightbox-caption">
              {images[lightboxIndex].nombre_original}
              <span>{lightboxIndex + 1} / {images.length}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPost;
